import { r, s } from '@/theme/tokens';
import {
  buildFilterAutocompleteListboxSx,
  filterDropdownIndicatorIconSizePx,
  filterInputSx,
  mergeFilterSlotSx,
} from './filterControlStyles';
import { buildTagsFilterDropdownPaperMotionSx } from './tagsFilterDropdownMotion';
import type { SxProps, Theme } from '@mui/material/styles';

type SxObject = Record<string, unknown>;

const isSxObject = (value: unknown): value is SxObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const resolveSxObject = (sx: SxProps<Theme>, theme: Theme): SxObject => {
  if (!sx) return {};
  const resolved: unknown = typeof sx === 'function' ? sx(theme) : sx;
  if (Array.isArray(resolved)) {
    return resolved.reduce<SxObject>((acc, item) => {
      if (isSxObject(item)) {
        return { ...acc, ...item };
      }
      return acc;
    }, {});
  }
  if (isSxObject(resolved)) {
    return resolved;
  }
  return {};
};

export const tagsFilterOptionHoverBackground = 'rgba(255, 255, 255, 0.055)';
export const tagsFilterSelectedOptionBackground = 'rgba(0, 128, 128, 0.2)';
export const tagsFilterPopperOverflowPaddingPx = 16;

type BuildTagsFilterControlHeightSxArgs = {
  controlHeight: number;
  endAdornmentPadding: string;
  inputBackgroundColor: string;
  inputLeftInset: string;
  selectedInputFlex: string;
  selectedInputWidth: string;
  shouldUseCompactSelectedInput: boolean;
};

export const buildTagsFilterControlHeightSx = ({
  controlHeight,
  endAdornmentPadding,
  inputBackgroundColor,
  inputLeftInset,
  selectedInputFlex,
  selectedInputWidth,
  shouldUseCompactSelectedInput,
}: BuildTagsFilterControlHeightSxArgs): SxProps<Theme> => ({
  '& .MuiAutocomplete-inputRoot': {
    minHeight: `${controlHeight}px !important`,
    height: `${controlHeight}px !important`,
    maxHeight: `${controlHeight}px !important`,
    backgroundColor: inputBackgroundColor,
    alignItems: 'center',
    flexWrap: 'nowrap !important',
    overflow: 'hidden',
    paddingTop: `${s('none')} !important`,
    paddingBottom: `${s('none')} !important`,
    paddingRight: `${endAdornmentPadding} !important`,
    minWidth: 0,
    cursor: 'pointer',
    '& .MuiAutocomplete-endAdornment': {
      right: s('sm'),
      display: 'flex',
      alignItems: 'center',
      gap: s('none'),
      '& > button + button': {
        marginLeft: `calc(${s('xxs')} - ${s('md')}) !important`,
      },
    },
    '& .MuiAutocomplete-clearIndicator': {
      margin: `${s('none')} !important`,
      marginRight: `${s('none')} !important`,
      padding: `${s('xxs')} !important`,
      width: 'auto',
      height: 'auto',
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
    },
    '& .MuiAutocomplete-popupIndicator': {
      padding: `${s('xs')} !important`,
      margin: `${s('none')} !important`,
      marginRight: `${s('xs')} !important`,
      width: 'auto',
      height: 'auto',
      '& .MuiSvgIcon-root': {
        width: `${filterDropdownIndicatorIconSizePx}px`,
        height: `${filterDropdownIndicatorIconSizePx}px`,
      },
    },
  },
  '& .MuiAutocomplete-input': {
    minHeight: `${controlHeight}px !important`,
    height: `${controlHeight}px !important`,
    minWidth: shouldUseCompactSelectedInput ? '0px !important' : undefined,
    width: `${selectedInputWidth} !important`,
    flex: selectedInputFlex,
    paddingTop: `${s('none')} !important`,
    paddingBottom: `${s('none')} !important`,
    paddingLeft: `${inputLeftInset} !important`,
    marginLeft: `${s('none')} !important`,
    lineHeight: '1 !important',
    cursor: 'pointer',
  },
});

type BuildTagsFilterAutocompleteSxArgs = BuildTagsFilterControlHeightSxArgs & {
  controlRadius: string;
  fontSizePx: number;
  hasActiveFilterValue: boolean;
  inputBorderColor?: string;
};

export const buildTagsFilterAutocompleteSx =
  ({
    controlHeight,
    controlRadius,
    endAdornmentPadding,
    fontSizePx,
    hasActiveFilterValue,
    inputBackgroundColor,
    inputBorderColor,
    inputLeftInset,
    selectedInputFlex,
    selectedInputWidth,
    shouldUseCompactSelectedInput,
  }: BuildTagsFilterAutocompleteSxArgs): SxProps<Theme> =>
  (theme) => ({
    ...resolveSxObject(filterInputSx, theme),
    ...resolveSxObject(
      buildTagsFilterControlHeightSx({
        controlHeight,
        endAdornmentPadding,
        inputBackgroundColor,
        inputLeftInset,
        selectedInputFlex,
        selectedInputWidth,
        shouldUseCompactSelectedInput,
      }),
      theme,
    ),
    width: '100%',
    minWidth: 0,
    '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
      ...theme.typography.body1,
      fontSize: `${fontSizePx}px`,
      borderRadius: `${controlRadius} !important`,
      cursor: 'pointer',
      color: 'var(--flowqa-filter-text)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: `${controlRadius} !important`,
      borderColor: hasActiveFilterValue ? theme.app.colors.primary : inputBorderColor,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.app.colors.primary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.app.colors.primary,
      borderWidth: '2px',
    },
    '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
      ...theme.typography.body1,
      fontSize: `${fontSizePx}px`,
      cursor: 'pointer',
      color: 'var(--flowqa-filter-text)',
    },
    '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
      {
        ...theme.typography.body1,
        fontSize: `${fontSizePx}px`,
        color: 'var(--flowqa-filter-placeholder)',
        opacity: 1,
      },
    '& .MuiAutocomplete-tag': {
      margin: 0,
      maxWidth: '100%',
      opacity: 1,
    },
    '& .MuiAutocomplete-tag.Mui-disabled': {
      opacity: 1,
    },
  });

type TagsFilterPopperModifier = {
  name: string;
  enabled?: boolean;
  options?: {
    altAxis?: boolean;
    fallbackPlacements?: string[];
    mainAxis?: boolean;
    padding?: number;
  };
};

export const buildTagsFilterPopperModifiers = (): TagsFilterPopperModifier[] => [
  {
    name: 'flip',
    enabled: true,
    options: {
      fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
      padding: tagsFilterPopperOverflowPaddingPx,
    },
  },
  {
    name: 'preventOverflow',
    options: {
      padding: tagsFilterPopperOverflowPaddingPx,
      mainAxis: false,
      altAxis: true,
    },
  },
];

type BuildTagsFilterPopperSxArgs = {
  dropdownMaxWidth: string;
  hasCountColumn: boolean;
  minWidth: string;
  optionGridTemplateColumns?: string;
  resolvedListboxWidth: string;
  width: string;
};

export const buildTagsFilterPopperSx = ({
  dropdownMaxWidth,
  hasCountColumn,
  minWidth,
  optionGridTemplateColumns,
  resolvedListboxWidth,
  width,
}: BuildTagsFilterPopperSxArgs): SxProps<Theme> => ({
  width: `${width} !important`,
  minWidth: `${minWidth} !important`,
  maxWidth: `${dropdownMaxWidth} !important`,
  zIndex: (theme) => theme.zIndex.modal,
  '& .MuiAutocomplete-paper': {
    minWidth,
    width,
    maxWidth: '100%',
  },
  '& .MuiAutocomplete-listbox': {
    width: `${resolvedListboxWidth} !important`,
  },
  '& .MuiAutocomplete-option': {
    ...(hasCountColumn
      ? {
          boxSizing: 'border-box',
          display: 'grid !important',
          gridTemplateColumns: optionGridTemplateColumns,
          width: '100% !important',
        }
      : {}),
  },
});

type BuildTagsFilterDropdownPaperSxArgs = {
  backgroundColor: string;
  controlRadius: string;
  dropdownMaxWidth: string;
  isDropdownClosing: boolean;
  minWidth: string;
  width: string;
};

export const buildTagsFilterDropdownPaperSx = ({
  backgroundColor,
  controlRadius,
  dropdownMaxWidth,
  isDropdownClosing,
  minWidth,
  width,
}: BuildTagsFilterDropdownPaperSxArgs): SxProps<Theme> => ({
  backgroundColor,
  border: '1px solid',
  borderColor: (theme) => theme.app.colors.border,
  borderRadius: `${controlRadius} !important`,
  overflow: 'hidden',
  boxShadow: 'none',
  backgroundImage: 'none',
  '--Paper-overlay': 'none',
  '--Paper-shadow': 'none',
  minWidth,
  width,
  maxWidth: dropdownMaxWidth,
  mt: s('none'),
  ...buildTagsFilterDropdownPaperMotionSx(isDropdownClosing),
});

export const tagsFilterClearIndicatorSx = {
  margin: `${s('none')} !important`,
  marginRight: `${s('none')} !important`,
  padding: `${s('xxs')} !important`,
  width: 'auto',
  height: 'auto',
  opacity: 1,
  visibility: 'visible',
  pointerEvents: 'auto',
  color: 'common.white',
} as const;

type BuildTagsFilterListboxSxArgs = {
  dropdownMaxWidth: string;
  hasCountColumn: boolean;
  minWidth: string;
  optionGridTemplateColumns?: string;
  optionHorizontalPadding: string;
  optionRightPadding: string;
  optionRowHeight: string;
  resolvedListboxWidth: string;
};

export const buildTagsFilterListboxSx = ({
  dropdownMaxWidth,
  hasCountColumn,
  minWidth,
  optionGridTemplateColumns,
  optionHorizontalPadding,
  optionRightPadding,
  optionRowHeight,
  resolvedListboxWidth,
}: BuildTagsFilterListboxSxArgs): SxProps<Theme> =>
  mergeFilterSlotSx(buildFilterAutocompleteListboxSx(optionRowHeight), {
    py: s('none'),
    minWidth,
    width: resolvedListboxWidth,
    maxWidth: dropdownMaxWidth,
    '& .MuiAutocomplete-option': {
      borderRadius: `${r('xs')} !important`,
      paddingLeft: `${optionHorizontalPadding} !important`,
      paddingRight: `${optionRightPadding} !important`,
      backgroundColor: 'transparent !important',
      color: 'text.primary',
      cursor: 'pointer',
      ...(hasCountColumn
        ? {
            boxSizing: 'border-box',
            display: 'grid !important',
            gridTemplateColumns: optionGridTemplateColumns,
            width: '100% !important',
          }
        : {}),
      whiteSpace: 'nowrap',
    },
    '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: `${tagsFilterOptionHoverBackground} !important`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: `${tagsFilterSelectedOptionBackground} !important`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
      {
        backgroundColor: `${tagsFilterSelectedOptionBackground} !important`,
      },
  });
